import React from 'react';
import {
  TextField,
  ReferenceField as RaReferenceField,
  Datagrid,
  useTranslate,
  ChipField,
  FunctionField,
} from 'react-admin';
import SvgIcon from '@material-ui/core/SvgIcon';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Chip from '@material-ui/core/Chip';
import { ShowController, DateField, StatusField, KycLevelField } from '~/components/ra';
import { PlusField, Link, ReferenceField } from '~/components';
import { Longdash } from '~/utils';
import { OpenIcon } from '~/img';
import { isEmpty } from 'lodash';

export const PricingDatagrid = (props: any) => {
  const { rowClick, showLinkToPricing, ...rest } = props;

  const translate = useTranslate();
  const t = (key: string) => translate(`resources.solutions.pricings.${key}`);

  const classes = useStyles();
  return (
    <Datagrid rowClick={rowClick} {...rest} selectedIds={null}>
      <FunctionField
        label={t('pricingPlan')}
        render={(record: any) =>
          !record ? (
            Longdash
          ) : (
            <div>
              {record.name}
              {record.format !== 'v2' && (
                <span className={classes.chipContainer}>
                  <Chip variant='outlined' size='small' label={t('v1')} className={classes.chip} />
                </span>
              )}
            </div>
          )
        }
        sortBy='name'
      />
      {showLinkToPricing && (
        <ShowController>
          {({ showRecord }) => {
            if (!showRecord) return Longdash;
            return (
              <Link
                href={`/pricings/${showRecord.id}/show`}
                target='_blank'
                onClick={(e: any) => e.stopPropagation()}
                className={classes.link}
              >
                {t('viewEdit')}
                <SvgIcon className={classes.icon}>
                  <OpenIcon title='show' />
                </SvgIcon>
              </Link>
            );
          }}
        </ShowController>
      )}
      <RaReferenceField
        source='solutionId'
        label={t('profileSolution')}
        reference='solutions'
        link={false}
        sortable={false}
      >
        <TextField source='name' allowEmpty emptyText={Longdash} />
      </RaReferenceField>
      <FunctionField
        label={t('paymentInstrument')}
        sortable={false}
        render={(record: any) => {
          if (!record) return Longdash;
          if (record.productId) {
            return (
              <ReferenceField
                record={{
                  id: record.productId,
                  type: 'PRODUCT',
                }}
                withLink={false}
                withLabel={false}
                className={classes.product}
              />
            );
          } else if (record.externalSourceType) {
            return <span>{record.externalSourceType}</span>;
          } else {
            return Longdash;
          }
        }}
        sortBy='paymentInstrument'
      />
      <DateField source='activeFrom' label={t('activeFrom')} emptyText={Longdash} format='LL' />
      <DateField source='activeTill' label={t('activeTill')} emptyText={Longdash} format='LL' />
      <StatusField
        source='accountStatus'
        label={t('profileStatus')}
        allowEmpty
        emptyText={Longdash}
        sortable={false}
      />
      <KycLevelField source='kycLevel' label={t('kycLevel')} sortable={false} sortBy='kycLevel' />
      <FunctionField
        sortable={false}
        label={t('profileTags')}
        render={(record: any) => {
          if (!record || isEmpty(record.profileTags)) return Longdash;
          return (
            <PlusField
              items={record.profileTags}
              mainComponent={(tag) => (
                <ChipField record={{ name: tag }} source='name' {...rest} key={tag} />
              )}
              tooltipsComponent={(tag) => (
                <ChipField record={{ name: tag }} source='name' {...rest} key={tag} />
              )}
            />
          );
        }}
      />
    </Datagrid>
  );
};

const useStyles = makeStyles((theme) => ({
  link: {
    position: 'relative',
    textDecoration: 'underline',
    textWrap: 'nowrap',
  },
  icon: {
    position: 'absolute',
    top: '-5px',
    right: '-19px',
    width: 14,
    height: 14,
  },
  chipContainer: {
    marginLeft: 5,
  },
  chip: {
    borderColor: theme.palette.warning.light,
    color: theme.palette.warning.light,
    borderRadius: 4,
    height: '18px',
    '& .MuiChip-label': {
      paddingLeft: '5px',
      paddingRight: '5px',
    },
  },
  product: {
    fontWeight: 400,
  },
}));
